import { useRef } from 'react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Chip,
  Stack,
  Typography,
} from '@locus-taxy/disco-ui';
import { User } from 'lucide-react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { getTestId } from '@locus-taxy/encore-utils';
import { useToast } from '@locus-taxy/encore-ui-toolkit';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode } from './profileTypes';
import { PHOTO_MAX_SIZE_BYTES, PHOTO_ACCEPTED_TYPES } from './profileConstants';

interface ProfileHeaderCardProps {
  readonly mode: ProfileViewMode;
  readonly fullName: string;
  readonly designation?: string;
  readonly roleName?: string;
  readonly photoUrl?: string | null;
  readonly photoLoading?: boolean;
  readonly onPhotoSelect?: (file: File) => void;
  readonly onPhotoRemove?: () => void;
  readonly hasPhoto?: boolean;
}

export function ProfileHeaderCard({
  mode,
  fullName,
  designation,
  roleName,
  photoUrl,
  photoLoading,
  onPhotoSelect,
  onPhotoRemove,
  hasPhoto,
}: ProfileHeaderCardProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError } = useToast();

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!PHOTO_ACCEPTED_TYPES.includes(file.type)) {
      showError(
        t(
          'dashboard.userDetails.invalidFileType',
          'Please upload a JPG or PNG file.'
        )
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > PHOTO_MAX_SIZE_BYTES) {
      showError(
        t(
          'dashboard.userDetails.fileTooLarge',
          'File size must be less than 1MB.'
        )
      );
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    onPhotoSelect?.(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const imageSrc = !photoLoading && photoUrl ? photoUrl : null;

  return (
    <div
      className="rounded-md border border-border bg-card"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'profile-header-card',
        page: 'account',
      })}
    >
      <Stack direction="row" align="center" gap={3} className="px-8 py-6">
        <Avatar size="lg">
          {imageSrc ? <AvatarImage src={imageSrc} alt={fullName} /> : null}
          <AvatarFallback>
            <User className="size-5" />
          </AvatarFallback>
        </Avatar>

        <Stack className="min-w-0 flex-1 gap-1">
          <Typography variant="h5">{fullName}</Typography>
          {designation && (
            <Typography variant="body1" color="muted">
              {designation}
            </Typography>
          )}
          {roleName && (
            <div>
              <Chip label={roleName} size="sm" variant="filled" />
            </div>
          )}
        </Stack>

        {mode === 'edit' && (
          <Stack align="end" gap={1} className="shrink-0">
            <Stack direction="row" gap={1}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                hidden
                onChange={handleFileChange}
                data-testid={getTestId({
                  action: TestIdAction.UPLOAD_PHOTO,
                  object: 'file-input',
                  page: 'account',
                })}
              />
              {hasPhoto && (
                <Button
                  variant="outline"
                  className="text-destructive border-destructive hover:bg-destructive/10"
                  onClick={() => onPhotoRemove?.()}
                  data-testid={getTestId({
                    action: TestIdAction.REMOVE_PHOTO,
                    object: 'button',
                    page: 'account',
                  })}
                >
                  {t('dashboard.userDetails.removePhoto', 'Remove Photo')}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleUploadClick}
                data-testid={getTestId({
                  action: TestIdAction.UPLOAD_PHOTO,
                  object: 'button',
                  page: 'account',
                })}
              >
                {hasPhoto
                  ? t('dashboard.userDetails.updatePhoto', 'Update Photo')
                  : t('dashboard.userDetails.uploadPhoto', 'Upload Photo')}
              </Button>
            </Stack>
            <Typography variant="caption" color="muted">
              {t(
                'dashboard.userDetails.photoConstraints',
                'JPG or PNG. Max 1MB.'
              )}
            </Typography>
          </Stack>
        )}
      </Stack>
    </div>
  );
}
